import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PleaseWaitPopup from "../../PleaseWaitPopup";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from "date-fns";
import { iconValues } from "../../../utils/iconValues";
import { Divider } from "@mui/material";
import { calulatedDiscountValue } from "../../../utils/summaryCalculation";
import Cookies from "js-cookie";
import { getQuotePdfSummary } from "../../../services/quotes";
import useTenant from "../../../components/hooks/useTenant";

const PrintQuotes = () => {
  const { handleTenantError }: any = useTenant();
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const navigate = useNavigate();
  const token = Cookies.get("token-jj-rental-ui");
  if (!token) {
    navigate('not-found');
  }

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');

  const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
  const [quoteData, setQuoteData] = useState<any>(null);
  const templateRef = useRef<HTMLDivElement>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showTemplate, setShowTemplate] = useState(true);
  const [updatedTaxList, setUpdatedTaxList] = useState<any[]>([]);
  const [totalBeforeDiscount, setTotalBeforeDiscount] = useState<number>(0.0);
  const [totalAfterDiscount, setTotalAfterDiscount] = useState<number>(0.0);
  const [allDataCalculated, setAllDataCalculated] = useState<boolean>(false);
  const [totalDays, setTotalDays] = useState<number>(0);
  const [onlyTotalDays, setOnlyTotalDays] = useState<number>(0);
  const [appliedDiscountList, setAppliedDiscountList] = useState<any[]>([
    {
      id: 0,
      discountId: 0,
      discountDefinition: "",
      discountType: "Percentage",
      discountAmount: 0.00,
      discount: 0.00,
      manualDiscount: false,
      discountApplied: false
    },
    {
      id: 0,
      discountId: 0,
      discountDefinition: "Manual Discount",
      discountType: "Amount",
      discountAmount: 0.00,
      discount: 0.00,
      manualDiscount: true,
      discountApplied: false
    }
  ]);

  useEffect(() => {
    if (id) {
      setPleaseWaitPopup(true);

      getQuotePdfSummary(id, tenantId)
        .then((res) => {
          const result = {
            ...res,
            pickupDate: format(res?.pickupDate, "EEEE, MMMM dd, yyyy @ hh:mm a"),
            returenDate: format(res?.returenDate, "EEEE, MMMM dd, yyyy @ hh:mm a"),
            createdDate: format(res?.createdDate, "MMMM dd, yyyy"),
          };
          setQuoteData(result);

          const addedTaxList = [];
          if (res?.mandatoryAdditionalChargesAmount && res?.mandatoryAdditionalChargesAmount > 0) {
            addedTaxList.push({
              taxName: "Additional Charge Fee",
              taxAmount: res?.mandatoryAdditionalChargesAmount ?? "",
              taxType: "Amount",
              selectionType: "One Time",
              basePrice: res?.mandatoryAdditionalChargesAmount
            });
          }

          if (res?.locationFeeAmount && res?.locationFeeAmount > 0) {
            addedTaxList.push({
              taxName: "Location Tax Fee",
              taxAmount: res?.locationFeePercentage ?? "",
              taxType: "Percent",
              selectionType: "One Time",
              basePrice: res?.locationFeeAmount
            });
          }

          if (res?.taxAmount && res?.taxAmount > 0) {
            addedTaxList.push({
              taxName: "Tax Fee",
              taxAmount: res?.taxPercentage ?? "",
              taxType: "Percent",
              selectionType: "One Time",
              basePrice: res?.taxAmount
            });
          }

          if (res?.governmentTaxAmount && res?.governmentTaxAmount > 0) {
            addedTaxList.push({
              taxName: "Government Tax Fee",
              taxAmount: res?.governmentTaxPercentage ?? "",
              taxType: "Percent",
              selectionType: "One Time",
              basePrice: res?.governmentTaxAmount
            });
          }

          setUpdatedTaxList(addedTaxList);


          let basePrice = 0;
          let totalOfSelectedVehicleClass = 0;
          if (res.totalMonths > 0) {
            basePrice += (res.totalMonths * res.monthlyRate);
          }

          if (res.totalWeeks > 0) {
            basePrice += (res.totalWeeks * res.weeklyRate);
          }

          if (res.totalDays > 0) {
            basePrice += (res.totalDays * res.dailyRate);
          }

          if (res.totalHours > 0) {
            basePrice += (res.totalHours * res.hourlyRate);
          }

          totalOfSelectedVehicleClass = basePrice;

          res?.quoteDiscountsForSummaryDto?.forEach((item: any) => {
            let discountPrice = 0;
            if (item?.discountType === "Amount") {
              discountPrice = item?.discountAmount ?? 0;
            } else {
              discountPrice = ((basePrice * (item?.discountAmount || 0)) / 100) || 0;
            }

            basePrice -= discountPrice;
          });

          setTotalAfterDiscount(basePrice);
          setTotalBeforeDiscount(totalOfSelectedVehicleClass);

          setTotalDays(res ? (res?.totalMonths * 30) + (res?.totalWeeks * 7) + res?.totalDays + (res?.totalHours > 0 ? 1 : 0) : 0);
          setOnlyTotalDays(res ? (res?.totalMonths * 30) + (res?.totalWeeks * 7) + res?.totalDays : 0);

          const couponDiscount = res?.quoteDiscountsForSummaryDto?.find((item: any) => !item?.manualDiscount);
          const manualDiscount = res?.quoteDiscountsForSummaryDto?.find((item: any) => item?.manualDiscount);

          let couponDiscountValue = 0.00;
          if (couponDiscount) {
            couponDiscountValue = calulatedDiscountValue(couponDiscount?.discountAmount, couponDiscount?.discountType, totalOfSelectedVehicleClass);
          }

          let manualDiscountValue = 0.00;
          if (manualDiscount) {
            manualDiscountValue = calulatedDiscountValue(manualDiscount?.discountAmount, manualDiscount?.discountType, totalOfSelectedVehicleClass);
          }

          setAppliedDiscountList([
            {
              id: couponDiscount ? couponDiscount?.id : 0,
              discountId: couponDiscount ? couponDiscount?.discountId : 0,
              discountDefinition: couponDiscount ? couponDiscount?.discountDefinition : 0,
              discountType: couponDiscount ? couponDiscount?.discountType : "Percentage",
              discountAmount: couponDiscount ? couponDiscount?.discountAmount : 0.00,
              discount: couponDiscountValue,
              manualDiscount: false,
              discountApplied: couponDiscount ? true : false
            },
            {
              id: manualDiscount ? manualDiscount?.id : 0,
              discountId: 0,
              discountDefinition: manualDiscount ? manualDiscount?.discountDefinition : 0,
              discountType: manualDiscount ? manualDiscount?.discountType : "Amount",
              discountAmount: manualDiscount ? manualDiscount?.discountAmount : 0.00,
              discount: manualDiscountValue,
              manualDiscount: true,
              discountApplied: manualDiscount ? true : false
            }
          ]);

          setAllDataCalculated(true);
          handleTenantError(null);
        })
        .catch((error) => {
          setPleaseWaitPopup(false);
          setQuoteData(null);
          handleTenantError(error);
        });
    } else {
      navigate(`not-found`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const generatePDF = async () => {
      if (!templateRef.current) return;

      const timer = setTimeout(async () => {
        const canvas = await html2canvas(templateRef.current!, {
          scale: 2,
          useCORS: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'pt', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        // Add more pages if needed
        while (heightLeft > 0) {
          position -= pdfHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdfHeight;
        }

        const blob = pdf.output('blob');
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPleaseWaitPopup(false);
        setShowTemplate(false);
      }, 1000); // Give DOM time to render

      return () => clearTimeout(timer);

    };

    if (allDataCalculated) generatePDF();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDataCalculated]);

  return (
    <div className="relative z-0 min-h-screen overflow-hidden">
      {showTemplate && (
        <div ref={templateRef} className="absolute top-[9999px] left-0 bg-white text-black w-[794px] p-6 text-xs font-sans"
        >
          {/* Header */}
          <div className="flex justify-center items-center border-b pb-4 mb-4">
            <div>
              <img src={quoteData?.companyLogo} alt="Company Logo" className="w-32 mb-2" />
            </div>
          </div>

          <div className="w-full mb-2 flex justify-between">
            <span className={`text-sm text-black`}>
              <strong>Quote Number: </strong>{quoteData?.id}
            </span>

            <span className={`text-sm text-black`}>
              <strong>Created At: </strong>{quoteData?.createdDate}
            </span>
          </div>

          {/* Customer Info */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Customer Information</h3>
            <p><strong>Name: </strong>{quoteData?.firstName + " " + quoteData?.lastName}</p>
            <p><strong>Email: </strong>{quoteData?.customerEmail}</p>
            <p><strong>Phone: </strong>{quoteData?.customerPhoneNumber}</p>
          </div>

          {/* Pickup Info */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Pickup</h3>
            <p><strong>Date: </strong>{quoteData?.pickupDate}</p>
            <p><strong>Location: </strong>{quoteData?.pickupLocationName}</p>
            <p><strong>Phone: </strong>{quoteData?.pickupLocationPhoneNumber}</p>
            <p><strong>Address: </strong>{quoteData?.pickupLocationAddress}</p>
          </div>

          {/* Return Info */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Return</h3>
            <p><strong>Date: </strong>{quoteData?.returenDate}</p>
            <p><strong>Location: </strong>{quoteData?.returnLocationName}</p>
            <p><strong>Phone: </strong>{quoteData?.returnLocationPhoneNumber}</p>
            <p><strong>Address: </strong>{quoteData?.returnLocationAddress}</p>
          </div>

          {/* vehicle Info */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Vehicle Information</h3><br />
            <div className={`flex justify-between bg-transparent p-2 w-full h-full min-h-max rounded-lg border border-gray-400`}>
              {/* Image */}
              <img
                src={quoteData?.vehicleClassImage}
                alt="vehicle"
                className="w-[150px] rounded-md"
              />

              {/* Info */}
              <div className="flex flex-col px-2 w-[34%] gap-1.5">
                <span className={`text-sm font-semibold text-black`}>
                  {quoteData?.vehicleClassLabel}
                </span>

                <div className={`flex flex-col items-start gap-1.5 text-xs text-black`}>
                  {
                    quoteData?.features
                      ?.sort((a: any, b: any) => a?.order - b?.order)
                      ?.map((feature: any, indexFeat: number) => {
                        const iconData = iconValues.find((icon) => icon.value === feature?.icon);
                        return (
                          <div key={indexFeat} className={`flex items-center gap-1 text-xs text-black-rgba`}>
                            {
                              iconData && (
                                <span className={`material-symbols-outlined text-[16px]`}>{iconData.icon}</span>
                              )
                            }
                            <span>{feature?.label}</span>
                          </div>
                        )
                      })
                  }
                </div>
              </div>

              {/* Price */}
              <div className="flex flex-col gap-1.5 w-[25%]">
                <div className="flex flex-col gap-1.5 text-left">
                  <p className={`text-sm text-black`}>
                    <span className="font-semibold">${parseInt(quoteData?.dailyRate)?.toFixed(2)}</span>
                    <span className={`text-sm font-normal text-black`}>/Day</span>
                  </p>

                  <p className={`text-sm text-black`}>
                    <span className="font-semibold"> $
                      {Number(totalBeforeDiscount)?.toFixed(2)}{" "}
                      total for {onlyTotalDays} Day{" "}
                      {quoteData?.totalHours > 0 && " and " + quoteData?.totalHours + " hours"}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div>
            <h3 className="font-semibold mb-2">Charge Information</h3><br />

            <div className="flex flex-col gap-1.5">
              {
                quoteData?.totalMonths !== 0 && (
                  <div className="flex justify-between items-start w-full">
                    <span className={`text-xs font-medium break-all flex-1 text-left text-black`}>{quoteData?.totalMonths}x Months</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}> ${quoteData?.monthlyRate?.toFixed(2)}</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}>${(quoteData?.totalMonths * quoteData?.monthlyRate)?.toFixed(2)}</span>
                  </div>
                )
              }

              {
                quoteData?.totalWeeks !== 0 && (
                  <div className="flex justify-between items-start w-full">
                    <span className={`text-xs font-medium break-all flex-1 text-left text-black`}>{quoteData?.totalWeeks}x Weeks</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}>${quoteData?.weeklyRate?.toFixed(2)}</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}>${(quoteData?.totalWeeks * quoteData?.weeklyRate)?.toFixed(2)}</span>
                  </div>
                )
              }

              {
                quoteData?.totalDays !== 0 && (
                  <div className="flex justify-between items-start w-full">
                    <span className={`text-xs font-medium break-all flex-1 text-left text-black`}>{quoteData?.totalDays}x Days</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}>${quoteData?.dailyRate?.toFixed(2)}</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}>${(quoteData?.totalDays * quoteData?.dailyRate)?.toFixed(2)}</span>
                  </div>
                )
              }

              {
                quoteData?.totalHours !== 0 && (
                  <div className="flex justify-between items-start w-full">
                    <span className={`text-xs font-medium break-all flex-1 text-left text-black`}>{quoteData?.totalHours}x Hours</span>
                    <span className={`text-xs font-medium break-all flex-1  text-right text-black`}>${quoteData?.hourlyRate?.toFixed(2)}</span>
                    <span className={`text-xs font-medium break-all flex-1 text-right text-black`}>${(quoteData?.totalHours * quoteData?.hourlyRate)?.toFixed(2)}</span>
                  </div>
                )
              }

              <Divider className={`w-full bg-gray-400`} />

              {
                appliedDiscountList
                  ?.filter((item: any) => item?.discountApplied)
                  ?.map((item: any, index: number) => {
                    return (
                      <div key={index} className="flex justify-between items-center">
                        <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{item?.discountDefinition}:</span>
                        <span className={`text-xs font-medium break-all w-[30%] text-right text-black`}>-${parseFloat(item?.discount)?.toFixed(2)}</span>
                      </div>
                    )
                  })
              }

              <div className="flex justify-between items-start">
                <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}></span>
                <span className={`text-xs font-medium break-all w-[30%] text-right text-black`}>${totalAfterDiscount?.toFixed(2)}</span>
              </div>

              {
                quoteData?.additionalCategoryForAgreement?.map((item: any, j: number) => {
                  return (
                    <div key={j}>
                      <span className={`text-xs font-bold text-black`}>{item?.categoryName}</span>

                      {
                        item?.additionalChargesForAgreement?.map((value: any, i: number) => (
                          <div key={i} className="flex justify-between items-center mb-2">
                            <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{value?.name} {value?.quantity > 1 ? value?.quantity + "x " : ''}:</span>
                            <span className={`text-xs font-medium break-all w-[30%] text-right text-black`}>
                              {
                                value?.selectionType?.toLowerCase() === "multiple"
                                  ? `${value?.chargeType?.toLowerCase() === "amount" ? "$" : ""}${((value?.basePrice ?? 0) / (totalDays || 1))?.toFixed(2)}${value?.chargeType?.toLowerCase() === "amount" ? " Day" : "% Day"}`
                                  : `${value?.chargeType?.toLowerCase() === "amount" ? "$" : ""}${(value?.basePrice ?? 0).toFixed(2)}${value?.chargeType?.toLowerCase() === "amount" ? "" : "%"}`
                              }
                            </span>
                            <span className={`text-xs font-medium break-all w-[30%] text-right text-black`}>${(value?.quantity * value?.basePrice)?.toFixed(2)}</span>
                          </div>
                        ))
                      }
                    </div>
                  )
                })
              }

              {
                updatedTaxList?.length > 0 && (
                  <div>
                    <span className={`text-xs font-bold text-black`}>Miscellaneous</span>
                    {
                      updatedTaxList?.map((item: any, index: number) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{item?.taxName}{`${item?.taxType === "Percent" ? ` (${item?.taxAmount}%)` : ""}`}:</span>
                          <span className={`text-xs font-medium break-all w-[30%] text-right text-black`}> ${parseFloat(item?.basePrice)?.toFixed(2)}</span>
                        </div>
                      ))
                    }
                  </div>
                )
              }
            </div>

            <Divider className={`w-full !mt-2 bg-gray-400`} />

            <div className={`flex justify-between items-center py-2`}>
              <span className={`text-xs font-bold text-black-rgba`}>Total Charges</span>
              <span className={`text-xs font-bold text-black`}> ${quoteData?.totalPrice?.toFixed(2) ?? 0.00}</span>
            </div>


            <Divider className={`w-full bg-gray-400`} />

            <div className={`flex justify-between items-center py-4`}>
              <span className={`text-xs font-bold text-black-rgba`}>Security Deposit

              </span>

              <span className={`text-xs font-bold text-red-500`}>${quoteData?.securityDepositAmount ? quoteData?.securityDepositAmount?.toFixed(2) : 0.00}</span>
            </div>
          </div>
        </div>
      )}

      {pdfUrl ? (
        <embed
          src={pdfUrl}
          type="application/pdf"
          width="100%"
          className="border shadow h-screen"
        />
      ) : (
        <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
      )}

    </div>
  )
}

export default PrintQuotes